import { useEffect, useMemo, useState } from "react";
import AppDrawer from "./AppDrawer";
import { Button, Input } from "reactstrap";
import { SelectPicker } from "rsuite";
import { modulesAtom } from "../../atoms/GlobalAtoms";
import { useAtomValue } from "jotai";
import { Field, Module } from "../../types/TenantInterfaces";
import useCreateDataTransforms from "../../api/api-hooks/modules/useCreateDataTransforms";
import { newToastEvent } from "../../utils/ToastUtil";
import { DataTransforms } from "../../types/TenantInterfaces";
import IconButton from "../../elements/IconButton";
import useModifyDataTransforms from "../../api/api-hooks/modules/useModifyDataTransforms";
import { invalidFields } from "../../utils/DataTransformsUtils";

interface Props {
  dataTransform?: DataTransforms;
  open: boolean;
  mode: "create" | "edit";
  closeDrawer: () => void;
}

const DataTransformsDrawer = ({
  open,
  dataTransform,
  mode,
  closeDrawer,
}: Props) => {
  const modules = useAtomValue(modulesAtom);
  const [formData, setFormData] = useState<DataTransforms | undefined>(
    structuredClone(dataTransform)
  );
  const [triggerMapping, setTriggerMapping] = useState<boolean>(
    mode === "edit" ? false : true
  );
  const [disbaled, setDisabled] = useState<boolean>(true);
  const { createDataTransforms } = useCreateDataTransforms();
  const { modifyDataTransforms } = useModifyDataTransforms();

  const dataSources: Array<{ label: string; value: string }> = useMemo(() => {
    if (!modules) return [];
    return modules
      .filter((module) => !module.Default)
      .map((module: any) => ({
        label: module.Name,
        value: module.APIName,
      }));
  }, [modules]);

  // Fields Available for the source module
  const sourceFields = useMemo(() => {
    if (!formData?.SourceModule) return null;
    return modules?.find((m) => m.APIName === formData.SourceModule)?.Fields;
  }, [formData?.SourceModule]);
  // Fields Available for the target module
  const targetFields = useMemo(() => {
    if (!formData?.TargetModule) return null;
    return modules?.find((m) => m.APIName === formData.TargetModule)?.Fields;
  }, [formData?.TargetModule]);

  const mappedFields = useMemo(() => {
    if (!formData?.SourceModule || !formData.TargetModule) return null;
    if (!sourceFields || !targetFields) return null;
    if (!triggerMapping) return null;
    return mappedTransformedFields(sourceFields, targetFields);
  }, [formData?.SourceModule, formData?.TargetModule]);

  useEffect(() => {
    if (!mappedFields || !targetFields || !sourceFields) return;
    if (!triggerMapping) return;
    const formFields: Array<{
      source: string;
      target: string;
      value?: string;
    }> = [];
    const indexQueue: number[] = [];

    Object.keys(mappedFields).forEach((key: string, i) => {
      const recomendedTargets = targetFields[mappedFields[key][0]].APIName;

      const sourceField = sourceFields.find((f) => f.Name === key);
      if (
        !sourceField ||
        !recomendedTargets ||
        indexQueue.includes(mappedFields[key][0])
      )
        return;
      formFields.push({
        source: sourceField.APIName,
        target: recomendedTargets,
        value: sourceField.FieldType === "SingleSelect" ? "" : undefined,
      });
      indexQueue.push(mappedFields[key][0]);
    });

    setFormData((prev: any) => ({
      ...prev,
      Fields: formFields,
      Name: `${formData?.SourceModule} to ${formData?.TargetModule}`,
    }));
  }, [
    mappedFields,
    dataTransform,
    formData?.SourceModule,
    formData?.TargetModule,
  ]);

  useEffect(() => {
    setDisabled(isDisabled());
  }, [formData]);

  return (
    <AppDrawer
      open={open}
      placement="right"
      size="md"
      backdrop="static"
      headerTitle="Data Transforms"
      headerDescription="Creat a new data transform from existing data sources."
      headerOptions={
        <Button
          onClick={saveDataTransforms}
          disabled={disbaled}
          color="primary"
        >
          Save
        </Button>
      }
      onClose={() => {
        setFormData(undefined);
        closeDrawer();
      }}
      body={
        <>
          <div className="d-flex  flex-column">
            <h4>Source</h4>
            <SelectPicker
              block
              onChange={(data: string | null) =>
                handleChange(data, "SourceModule")
              }
              value={formData?.SourceModule}
              data={dataSources}
              placeholder="Select Source"
            />
          </div>
          <div className="d-flex mt-4 flex-column">
            <h4>Target</h4>
            <SelectPicker
              block
              onChange={(data: string | null) =>
                handleChange(data, "TargetModule")
              }
              data={dataSources.filter(
                (dS) => dS.value !== formData?.SourceModule
              )}
              value={formData?.TargetModule}
              disabled={!formData?.SourceModule}
              placeholder="Select Target"
            />
          </div>
          {modules &&
            formData?.SourceModule &&
            formData?.TargetModule &&
            formData.Fields && (
              <div className="mt-4">
                <div className="d-flex mb-4 flex-column">
                  <h4>Name</h4>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={formData?.Name}
                    defaultValue={
                      formData?.Name
                        ? undefined
                        : `Convert ${formData?.SourceModule} to ${formData?.TargetModule}`
                    }
                    placeholder={"Name"}
                    onChange={(e) =>
                      setFormData({ ...formData, Name: e.target.value })
                    }
                  />
                </div>
                <h4 className="mb-0">Field Mapping</h4>
                <div className="small mb-4">
                  Fields will be mapped as shown upon transforming the records
                  source type to the target record type.
                </div>
                <DataTransformsForm
                  // @ts-ignore
                  source={modules?.find(
                    (module: any) => module?.APIName === formData?.SourceModule
                  )}
                  // @ts-ignore
                  target={modules?.find(
                    (module: any) => module?.APIName === formData?.TargetModule
                  )}
                  formData={formData}
                  setFormData={setFormData}
                  onChange={(
                    value: string,
                    key: "source" | "target" | "value",
                    index: number
                  ) => {
                    setFormData((prev?: DataTransforms) => {
                      if (!prev) return;
                      prev.Fields[index][key] = value;
                      return { ...prev };
                    });
                    if (isDisabled()) setDisabled(false);
                    else setDisabled(true);
                  }}
                  onAddField={onAddField}
                  onRemoveField={(index: number) => {
                    setFormData((prev?: DataTransforms) => {
                      if (!prev) return;
                      prev.Fields.splice(index, 1);
                      return { ...prev };
                    });
                  }}
                />
              </div>
            )}
        </>
      }
    />
  );

  function filterFields(fields: Field[]) {
    return fields
      .filter(
        (field) =>
          invalidFields.some((invalid) => field.Name === invalid) === false
      )
      .filter(
        (field) =>
          ["Subitem", "MultiSubitem"].includes(field.FieldType) === false
      )
      .map((field: Field) => {
        if (field.FieldType === "Subitem") {
          return (
            field?.SubitemFields?.map(
              (subField: any) => `${field.Name}.${subField.Name}`
            ) || []
          );
        }
        return field.Name;
      })
      .flat();
  }

  function mappedTransformedFields(
    sourceFields: Field[],
    targetFields: Field[]
  ) {
    // Gett all the fields available for Source module including subitems
    const sourceNames = filterFields(sourceFields);

    // Get all the fields available for Target module
    const targetNames = filterFields(targetFields);

    if (!sourceNames || !targetNames) return [];

    // Mapping the fields based on the name
    const mappRelatedByName = sourceNames.reduce((object: any, fieldStr) => {
      const relatedByName = findRelatedName(fieldStr, targetNames);
      return relatedByName.length > 0
        ? {
            ...object,
            [fieldStr]: relatedByName,
          }
        : object;
    }, {});

    // Mapping the fields based on the type
    return Object.keys(mappRelatedByName).reduce((object: any, key) => {
      const relatedByType = findRelatedTypes(key, mappRelatedByName[key]);

      return relatedByType.length > 0
        ? {
            ...object,
            [key]: relatedByType,
          }
        : object;
    }, {});
  }

  function findRelatedName(fieldName: string, arr: string[]): number[] {
    const words = new Set(fieldName.split(" "));
    const relatedIndexes = arr.flatMap((s, i) => {
      const wordsInString = new Set(s.split(" "));
      return [...wordsInString].some((word) => words.has(word)) ? i : [];
    });
    return relatedIndexes;
  }

  function findRelatedTypes(fieldName: string, arr: number[]): number[] {
    if (!sourceFields || !targetFields) return [];
    const foundField = sourceFields?.find((f) => f.Name === fieldName);
    if (!foundField) return [];
    const out: number[] = [];
    arr.forEach((i) => {
      // @ts-ignore
      if (targetFields[i]?.FieldType === "SingleReference") {
        if (foundField?.SourcesList?.[0] === targetFields[i]?.SourcesList?.[0])
          out.push(i);
      }

      targetFields[i]?.FieldType === foundField?.FieldType ? out.push(i) : null;
    });

    return out;
  }

  function handleChange(
    value: string | null,
    key: "SourceModule" | "TargetModule"
  ) {
    setTriggerMapping(true);

    setFormData((prev: any) => ({
      ...(prev ?? {}),
      [key]: value,
    }));
  }

  function saveDataTransforms() {
    if (!formData) return;
    if (mode === "edit") {
      return modifyDataTransforms({
        moduleAPIName: formData.SourceModule,
        dataTransforms: formData,
        onSuccess: () => {
          newToastEvent("success", "Data Transforms updated successfully");
          closeDrawer();
        },
        onError: () => {
          newToastEvent("error", "Something went wrong");
        },
      });
    }

    createDataTransforms({
      moduleAPIName: formData.SourceModule,
      dataTransforms: {
        ...formData,
        Name:
          formData.Name ??
          ` ${formData.SourceModule} to ${formData.TargetModule}`,
      },
      onSuccess: () => {
        newToastEvent("success", "Data Transforms created successfully");
        setFormData(undefined);
        closeDrawer();
      },
      onError: () => {
        setFormData(undefined);
        newToastEvent("error", "Something went wrong");
        closeDrawer();
      },
    });
  }

  function isDisabled(): boolean {
    if (!formData) return true;
    if (!formData.SourceModule || !formData.TargetModule) return true;
    if (!formData.Fields || formData.Fields.length === 0) return true;
    let flag = true;
    for (let i = 0; i < formData.Fields.length; i++) {
      if (
        formData.Fields[i].source &&
        formData.Fields[i].target &&
        ![null, ""].includes(formData.Fields[i].value)
      ) {
        flag = false;
      } else {
        flag = true;
        break;
      }
    }

    return flag;
  }

  function onAddField() {
    setFormData((prev) => {
      if (!prev) return;
      return {
        ...prev,
        Fields: [
          ...(prev.Fields ?? []),
          {
            source: "",
            target: "",
          },
        ],
      };
    });
    setDisabled(true);
  }
};

interface DataTransformsFormProps {
  source: Module;
  target: Module;
  formData: DataTransforms;
  onChange: (
    value: string,
    key: "source" | "target" | "value",
    index: number
  ) => void;
  onAddField: () => void;
  onRemoveField: (index: number) => void;
}

const DataTransformsForm = ({
  source,
  target,
  formData,
  onChange,
  onAddField,
  onRemoveField,
}: DataTransformsFormProps) => {
  const sourceFields = source?.Fields;
  const targetFields = target?.Fields;

  const sourcePickerOptions =
    sourceFields
      ?.filter(
        (field) =>
          !invalidFields.includes(field.Name) &&
          field.FieldType !== "MultiSubitem"
      )
      ?.map((field: Field) => {
        if (field.FieldType === "Subitem") {
          return (
            field.SubitemFields?.filter(
              (subField) => !invalidFields.includes(subField.Name)
            ).map((subField: any) => {
              return {
                label: `${field.Name} - ${subField.Name}`,
                value: `${field.APIName}.${subField.APIName}`,
              };
            }) || []
          );
        }
        return {
          label: field.Name,
          value: field.APIName,
        };
      })
      .flat() || [];

  sourcePickerOptions.unshift({
    label: `The ${source?.Name} Record`,
    value: `{{record.id}}+${source?.APIName}`,
  });

  const disabledSource = useMemo(() => {
    if (!formData?.Fields) return [];
    return formData?.Fields.map((f) => f.source);
  }, [formData]);

  const disabledTarget = useMemo(() => {
    if (!formData?.Fields) return [];
    return formData?.Fields.map((f) => f.target);
  }, [formData]);

  return (
    <div className="m-0 p-0 w-100">
      <div style={{ width: "95%" }} className="d-flex jtify-content-between">
        <div style={{ width: "47%" }}>
          <h4 className="m-0">Source</h4>
          <small>Source Fields</small>
        </div>
        <div style={{ width: "6%" }}></div>
        <div style={{ width: "47%" }}>
          <h4 className="m-0">Target</h4>
          <small>Target Fields</small>
        </div>
      </div>

      {formData &&
        formData.Fields.length > 0 &&
        formData?.Fields?.map((f, i) => {
          return (
            <FieldPicker
              key={`${f.source}-${f.target}-${i}`}
              // @ts-ignore
              field={f}
              targetFields={targetFields.filter(
                (field) => !invalidFields.includes(field.Name)
              )}
              sourceFields={sourceFields.filter(
                (field) => !invalidFields.includes(field.Name)
              )}
              index={i}
              sourcePicker={sourcePickerOptions}
              onRemoveField={onRemoveField}
              disabledSource={disabledSource}
              disabledTarget={disabledTarget}
              onChange={onChange}
            />
          );
        })}

      <Button onClick={onAddField} size="sm mt-4">
        <i style={{ fontSize: 14 }} className="mdi mdi-plus" />
        Add Fields
      </Button>
    </div>
  );
};

interface FieldPickerProps {
  field: { source: string; target: string; value: string };
  index: number;
  sourcePicker: Array<{ label: string; value: string }>;
  targetFields: Field[];
  sourceFields: Field[];
  disabledSource: string[];
  disabledTarget: string[];
  onChange: (
    value: string,
    key: "source" | "target" | "value",
    index: number
  ) => void;
  onRemoveField: (index: number) => void;
}

const FieldPicker = ({
  index,
  sourcePicker,
  field,
  targetFields,
  sourceFields,
  disabledSource,
  disabledTarget,
  onChange,
  onRemoveField,
}: FieldPickerProps) => {
  const [source, setSource] = useState(field.source.trim().replace("-", "."));

  const sourceField = useMemo(() => {
    if (source.startsWith("{{record.id}}")) {
      return {
        APIName: "{{record.id}}",
        FieldType: "SingleReference",
        SourcesList: [`Tenant.Modules.${source.split("+")[1]}`],
      };
    }

    if (source.includes(".")) {
      const [parent, child] = source.split(".");
      const subitemFields = sourceFields.find((s) => s.APIName === parent);
      return subitemFields?.SubitemFields?.find((s) => s.APIName === child);
    }
    return sourceFields.find((s) => s.APIName === source);
  }, [source]);

  const targetOptions = useMemo(() => {
    const availableFields = targetFields.filter((Tf) => {
      if (
        sourceField?.FieldType === "SingleSelect" &&
        ["Text", "SingleSelect"].includes(Tf?.FieldType || "")
      )
        return true;
      if (
        sourceField?.FieldType === "Number" &&
        ["Text", "Number"].includes(Tf?.FieldType || "")
      )
        return true;

      if (
        sourceField?.FieldType === "Text" &&
        ["Text", "LongText"].includes(Tf?.FieldType || "")
      )
        return true;

      if (
        sourceField?.FieldType === "Percent" &&
        ["Text", "LongText", "Percent"].includes(Tf?.FieldType || "")
      )
        return true;

      if (
        ["SingleFile", "Audio", "Image", "Video"].includes(
          sourceField?.FieldType || ""
        ) &&
        ["SingleFile"].includes(Tf?.FieldType || "")
      )
        return true;

      if (
        sourceField?.FieldType === "SingleReference" &&
        Tf?.FieldType === "SingleReference"
      ) {
        const sFSourcesList = Array.isArray(sourceField?.SourcesList)
          ? sourceField.SourcesList.toString()
          : sourceField.SourcesList;
        const tFSourcesList = Array.isArray(Tf?.SourcesList)
          ? Tf.SourcesList.toString()
          : Tf.SourcesList;
        if (sFSourcesList === tFSourcesList) return true;
      }

      return Tf.FieldType === sourceField?.FieldType;
    });

    return availableFields
      .map((field) => {
        if (field.FieldType === "Subitem") {
          return (
            field.SubitemFields?.map((subField: any) => {
              return {
                label: `${field.Name} - ${subField.Name}`,
                value: `${field.APIName}.${subField.APIName}`,
              };
            }) || []
          );
        }
        return {
          label: field.Name,
          value: field.APIName,
        };
      })
      .flat();
  }, [sourceField]);

  const targetField = useMemo(() => {
    return targetFields.find((t) => t.APIName === field.target);
  }, [field.target]);

  return (
    <div className="d-flex jutify-content-between my-2">
      <div className="d-flex" style={{ width: "95%" }}>
        <SelectPicker
          data={sourcePicker}
          preventOverflow
          style={{ width: "47%" }}
          searchable={false}
          placeholder="Select source field"
          value={field.source}
          onChange={(value) => {
            // @ts-ignore
            onChange(value || "", "source", index);
            onChange("", "target", index);
            // @ts-ignore
            setSource(value);
          }}
          onClean={() => onChange("", "target", index)}
          disabledItemValues={disabledSource}
        />
        <div className="d-flex justify-content-center" style={{ width: "6%" }}>
          <i
            style={{ fontSize: 26 }}
            className="mdi text-primary mdi-arrow-right-thick"
          />
        </div>

        {targetField?.FieldType !== "SingleSelect" && (
          <SelectPicker
            disabled={!field.source}
            data={targetOptions}
            preventOverflow
            style={{ width: "47%" }}
            searchable={false}
            placeholder="Select target field"
            value={field.target}
            // @ts-ignore
            onChange={(value) => onChange(value, "target", index)}
            disabledItemValues={disabledTarget}
          />
        )}
        {targetField?.FieldType === "SingleSelect" && (
          <div style={{ width: "47%" }}>
            <SelectPicker
              disabled={!field.source}
              data={targetOptions}
              preventOverflow
              style={{ width: "46%", marginRight: "2%" }}
              searchable={false}
              placeholder="Select target field"
              value={field.target}
              // @ts-ignore
              onChange={(value) => onChange(value, "target", index)}
            />
            <SelectPicker
              disabled={!field.source}
              // @ts-ignore
              data={
                Array.isArray(targetField?.SourcesList)
                  ? targetField?.SourcesList?.map((s: any) => ({
                      label: s.Name,
                      value: s.Value,
                    }))
                  : [
                      {
                        label: targetField?.SourcesList,
                        value: targetField.SourcesList,
                      },
                    ]
              }
              preventOverflow
              style={{ width: "46%", marginLeft: "2%" }}
              searchable={false}
              placeholder="Select Value"
              value={field?.value || ""}
              // @ts-ignore
              onChange={(value) => onChange(value, "value", index)}
            />
          </div>
        )}
      </div>
      <div className="ml-1" style={{ width: "5%" }}>
        <IconButton
          color="danger"
          onClick={() => onRemoveField(index)}
          icon="mdi mdi-delete"
        />
      </div>
    </div>
  );
};

export default DataTransformsDrawer;
